import React, { useMemo, useState } from 'react';
import { Camp } from '../../utils/types';
import { useAddCampToSchedule, useRemoveCampFromSchedule, useCreateBooking } from '../../api';
import './CampCardCompact.css';
import { useCampContext } from '../../context/CampContext';
import { useUserContext } from '../../context/UserContext';
import { LoginModalProvider } from '../../context/LoginModalContext';
import LoginModal from '../login/LoginModal';
import { useNotifications } from '../../context/NotificationContext';
import CampDatesDropdown from './CampDatesDropdown';
import { ExternalLink, Heart, Clock, CircleDollarSign, Zap } from 'lucide-react';
import { CampFormatUtils } from '../../utils/campFormatUtils';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { useAnalytics } from '../../context/AnalyticsContext';
import { useFeatureFlag } from '../../context/FeatureFlagContext';
import { FeatureFlags } from '../../utils/featureFlags';
import { useNavigate } from 'react-router-dom';

interface CampCardCompactProps {
	camp: Camp;
	selectedDate?: string;
	onDateChange?: (campId: string, dateId: string) => void;
	onScheduleAdd?: (beginDate: Date) => void;
	favoriteId?: string;
	scheduledDateIds?: string[];
}

const CampCardCompact: React.FC<CampCardCompactProps> = ({
	camp,
	selectedDate,
	onDateChange,
	onScheduleAdd,
	favoriteId,
	scheduledDateIds
}) => {
	const [showRegistrationModal, setShowRegistrationModal] = useState(false);
	const addCampToSchedule = useAddCampToSchedule();
	const removeCampFromSchedule = useRemoveCampFromSchedule();
	const createBooking = useCreateBooking();
	const { setSchedule, currentScheduleUuid } = useCampContext();
	const { isLoggedIn } = useUserContext();
	const { showSuccess, showError } = useNotifications();
	const { trackEvent } = useAnalytics();
	const { isInFavorites, handleFavoriteToggle, isPending } = useFavoriteToggle();
	const navigate = useNavigate();
	const instantBookingEnabled = useFeatureFlag(FeatureFlags.INSTANT_BOOKING, false);

	const handleRegister = (email: string) => {
		setShowRegistrationModal(false);
		showSuccess("You have successfully registered! You can now add favorites and add camps to your schedule.");
	};

	const handleLogin = (email: string) => {
		setShowRegistrationModal(false);
		showSuccess("You have successfully logged in! You can now add favorites and add camps to your schedule.");
	};

	const handleFavoriteClick = (e: React.MouseEvent, camp: Camp, favoriteId: string) => {
		e.stopPropagation();
		trackEvent('schedule_page_camp_card_save_clicked', { camp_id: camp.id, is_logged_in: isLoggedIn });
		if (!isLoggedIn) {
			setShowRegistrationModal(true);
			return;
		}
		handleFavoriteToggle(camp, favoriteId);
	};

	const processBooking = async () => {
		const useInstantBooking = instantBookingEnabled && camp.organizer?.instant_booking;

		if (!useInstantBooking) {
			if (camp.website) {
				window.open(camp.website, '_blank', 'noopener,noreferrer');
			}
			return;
		}

		try {
			const campDateIds = effectiveSelectedDate ? [effectiveSelectedDate] : [];
			const booking = await createBooking.mutateAsync({
				camp_id: camp.id,
				camp_date_ids: campDateIds
			});
			navigate(`/checkout?booking_uuid=${booking.uuid}`, { state: { booking } });
		} catch (error) {
			showError('Failed to create booking. Please try again.', error as Error);
		}
	};

	const handleBookNow = async (e: React.MouseEvent) => {
		e.stopPropagation();
		trackEvent('schedule_page_camp_card_book_now_clicked', { camp_id: camp.id });
		await processBooking();
	};

	const handleScheduleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isLoggedIn) {
			setShowRegistrationModal(true);
			return;
		}

		if (!currentScheduleUuid) {
			showError('No schedule selected', new Error('Please select a schedule before adding camps.'));
			return;
		}

		if (isEffectivelyScheduled) {
			removeCampFromSchedule.mutate(
				{ scheduleUuid: currentScheduleUuid, campId: camp.id, campDateId: effectiveSelectedDate },
				{
					onSuccess: (data) => {setSchedule(data); showSuccess('Camp removed from schedule'); },
					onError: (error) => {showError('Failed to remove camp from schedule. Please try again.', error); }
				}
		);
		} else {
			addCampToSchedule.mutate(
				{ scheduleUuid: currentScheduleUuid, campId: camp.id, campDateId: effectiveSelectedDate },
				{
					onSuccess: (data) => {
					setSchedule(data);
					showSuccess('Camp added to schedule');

					const addedCamp = data.scheduled_camps.find(
						(scheduledCamp: any) =>
							scheduledCamp.camp.id === camp.id &&
							scheduledCamp.camp_date.id === effectiveSelectedDate
					);

					if (addedCamp?.camp_date?.start_date) {
						const beginDate = new Date(addedCamp.camp_date.start_date);
						onScheduleAdd?.(beginDate);
					}
				},
				onError: (error) => {showError('Failed to add camp to schedule. Please try again.', error); }
			});
		}
	};

	const effectiveSelectedDate = useMemo(() => {
		// If explicitly selected, use that
		if (selectedDate) return selectedDate;

		// If only one date, auto-select it
		if (camp.camp_dates.length === 1) return camp.camp_dates[0].id;

		// If multiple dates and some are scheduled, default to first scheduled one
		if (scheduledDateIds && scheduledDateIds.length > 0) {
			const firstScheduledDate = camp.camp_dates.find(cd => scheduledDateIds.includes(cd.id));
			if (firstScheduledDate) return firstScheduledDate.id;
		}

		// Otherwise, default to first date
		if (camp.camp_dates.length > 0) return camp.camp_dates[0].id;

		return '';
	}, [selectedDate, camp.camp_dates, scheduledDateIds]);

	const isEffectivelyScheduled = Boolean(effectiveSelectedDate && scheduledDateIds?.includes(effectiveSelectedDate));

	const selectedCampDate = useMemo(() => {
		return camp.camp_dates.find(cd => String(cd.id) === effectiveSelectedDate);
	}, [camp.camp_dates, effectiveSelectedDate]);

	// Note: current_capacity is the number of bookings already made, so available = max_capacity - current_capacity
	const isAtCapacity = Boolean(selectedCampDate && selectedCampDate.max_capacity - selectedCampDate.current_capacity <= 0);

	return (
		<div className="camp-card-compact">
			<div className="camp-card-compact-header">
				<h3 className="camp-card-compact-title">{camp.name}</h3>
				<button
					className="camp-card-compact-favorite"
					aria-label={isInFavorites(camp) ? 'Remove from favorites' : 'Add to favorites'}
					title={isInFavorites(camp) ? 'Remove from favorites' : 'Add to favorites'}
					onClick={(e) => handleFavoriteClick(e, camp, '1')}
					disabled={isPending}
				>
					<Heart className="icon icon--sm" fill={isInFavorites(camp) ? 'currentColor' : 'none'}/>
				</button>
			</div>

			<p className="camp-organizer-name">From: {camp.organizer?.name}</p>

			<div className="camp-details">
				<div className="camp-detail-item">
					<CircleDollarSign className="icon icon--sm" />
					<span className="detail-text">{CampFormatUtils.getPriceDisplay(camp)}</span>
				</div>
				<div className="camp-detail-item">
					<Clock className="icon icon--sm" />
					<span className="detail-text">{CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
				</div>
			</div>

			<div className="camp-card-compact-date">
				<CampDatesDropdown
					camp={camp}
					selectedDate={selectedDate}
					onDateChange={onDateChange}
					scheduledDateIds={scheduledDateIds}
				/>
			</div>

			<div className="camp-card-compact-actions">
				<button
					className="btn btn--secondary btn--sm"
					onClick={handleScheduleToggle}
				>
					{isEffectivelyScheduled ? 'Remove from Schedule' : 'Add to Schedule'}
				</button>
				{instantBookingEnabled && camp.organizer?.instant_booking ? (
					<button
						className="btn btn--instant-booking btn--sm"
						onClick={handleBookNow}
						disabled={!effectiveSelectedDate || isAtCapacity}
					>
						{isAtCapacity ? (
							'Camp Full'
						) : (
							<><Zap className="icon icon--sm" /> Book Now</>
						)}
					</button>
				) : (
					<button
						className="btn btn--primary btn--sm"
						onClick={handleBookNow}
						disabled={!camp.website || !effectiveSelectedDate}
					>
						Book Now <ExternalLink className="icon icon--sm" />
					</button>
				)}
			</div>

			{showRegistrationModal && (
				<LoginModalProvider initialStep="register">
					<LoginModal onClose={() => setShowRegistrationModal(false)} onLogin={handleLogin} onRegister={handleRegister} />
				</LoginModalProvider>
			)}
		</div>
	);
};

export default CampCardCompact;
